// features/admin/rewardApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface Reward {
  id: number;
  reward_name: string;
  amount: string;
  points: string;
  created_at?: string;
}

export interface RewardFormData {
  reward_name: string;
  amount: string;
  points: string;
  id?: number;
}

export interface RewardResponse {
  error: boolean;
  message: string;
  data?: Reward[];
}

export interface RewardRequest {
  reward_name: string;
  amount: string;
  points: string;
}

export interface UpdateRewardRequest extends RewardRequest {
  id: number;
}

export interface DeleteRewardRequest {
  id: number;
}

// Validation helper
export const validateAmount = (value: string): boolean => {
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0;
};

export const validatePoints = (value: string): boolean => {
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0;
};

export const rewardApi = createApi({
  reducerPath: 'rewardApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      headers.set('Content-Type', 'application/x-www-form-urlencoded');
      return headers;
    },
  }),
  tagTypes: ['Reward'],
  endpoints: (build) => ({
    listRewards: build.query<RewardResponse, void>({
      query: () => ({
        url: 'user/rewardlist',
        method: 'POST',
        body: new URLSearchParams(),
      }),
      providesTags: ['Reward'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || '',
        data: response.data || [],
      }),
    }),

    addReward: build.mutation<RewardResponse, RewardRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('reward_name', data.reward_name.trim());
        formData.append('amount', data.amount.trim());
        formData.append('points', data.points.trim());

        return {
          url: 'user/rewardadd',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Reward'],
    }),

    updateReward: build.mutation<RewardResponse, UpdateRewardRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('id', data.id.toString());
        formData.append('reward_name', data.reward_name.trim());
        formData.append('amount', data.amount.trim());
        formData.append('points', data.points.trim());

        return {
          url: 'user/rewardupdate',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Reward'],
    }),

    deleteReward: build.mutation<RewardResponse, DeleteRewardRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('id', data.id.toString());

        return {
          url: 'user/rewarddelete',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Reward'],
    }),
  }),
});

export const {
  useListRewardsQuery,
  useAddRewardMutation,
  useUpdateRewardMutation,
  useDeleteRewardMutation,
} = rewardApi;